import React from 'react';
import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../redux';
import {
  fetchOperationsKpiDetails,
  resetKpiDashboard,
} from '../redux/OperationsReducer/operationsSlice';
import { selectUserDetails, selectIsMultiStoreEnabled } from '../redux/UserReducer/userSlice';
import { getEffectiveStoreNumber } from '../utils/appUtils';

/**
 * Custom hook to manage operations KPI data fetching and reset logic
 * Handles:
 * - Fetching KPI data when component mounts
 * - Fetching KPI data when store number or multi-store mode changes
 * - Does NOT retry on error - user must refresh page or navigate away and back
 *
 * @returns void - Side effects only (fetching and resetting data)
 */
export const useOperationsKpiData = (): void => {
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector(selectUserDetails);
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);

  const effectiveStoreNumber = useMemo(
    () => getEffectiveStoreNumber(userDetails, isMultiStoreEnabled),
    [userDetails, isMultiStoreEnabled],
  );

  // Fetch KPI data only when store number or multistore flag changes
  // Does NOT refetch on error - prevents infinite retry loop
  React.useEffect(() => {
    if (effectiveStoreNumber) {
      dispatch(resetKpiDashboard());
      dispatch(
        fetchOperationsKpiDetails({
          storeNumber: effectiveStoreNumber,
          isMultiStoreMode: isMultiStoreEnabled,
        }),
      );
    }
  }, [dispatch, effectiveStoreNumber, isMultiStoreEnabled]);
};
